
    import {defineComponent, ref, computed, watch, nextTick, PropType} from 'vue';
    import debounce from 'lodash-es/debounce';
    import VueSelect from 'vue-select';
    import {VJoyFormError, VJoyIcon} from '@maltjoy/core-vue';

    export default defineComponent({
        name: 'Autocomplete',
        components: {
            VueSelect,
        },
        emits: ['update:modelValue', 'focus', 'autocomplete:select', 'autocomplete:clear'],
        props: {
            placeholder: {
                type: String,
                default: 'Paris, île-de-France, France',
            },
            focusAfterSelect: {
                type: Boolean,
                default: false,
            },
            dataTestId: String,
            clearSearchAfterSelection: {
                type: Boolean,
                default: false,
            },
            location: {
                type: Boolean,
                default: false,
            },
            noOptionsLabel: String,
            modelValue: String,
            label: {
                type: String,
                default: 'name',
            },
            autocompleteService: {
                type: Object,
                required: true,
            },
            invalid: Boolean,
            excludeOptions: {
                type: Object as PropType<Array<string>>,
            },
        },
        setup(props, {emit}) {
            let loadingFunction: any = null;
            const selected = ref<string | null>(null);
            const query = ref<string | null>(null);
            const options = ref<string[]>([]);
            const debouncedQuery = debounce(search, 350);
            const noOptions = ref<boolean>(false);
            const select = ref<InstanceType<typeof VueSelect> | null>(null);

            const iconName = computed(() => (props.location ? 'location' : 'search'));

            watch(
                () => props.modelValue,
                () => {
                    selected.value = props.modelValue || null;
                },
                {immediate: true},
            );

            function onSearch(search: any, loading: any): void {
                if (search.length > 0) {
                    loading(true);
                    loadingFunction = loading;
                    query.value = search;
                    debouncedQuery();
                } else {
                    options.value = [];
                    query.value = null;
                }
            }

            async function search(): Promise<void> {
                let [data, error] = await props.autocompleteService.search(query.value);
                if (data) {
                    if (props.excludeOptions?.length) {
                        data = data.filter((val: any) => !props.excludeOptions!.includes(val));
                    }
                    loadingFunction(false);
                    options.value = data;
                    noOptions.value = options.value.length === 0;
                } else if (error) {
                    loadingFunction(false);
                    noOptions.value = true;
                }
            }

            function onSearchBlur(): void {
                noOptions.value = false;
            }

            function onFocus(): void {
                emit('focus');
            }

            function clearSelection(): void {
                selected.value = '';
                select.value?.clearSelection();
            }

            function handleSelectValue(element: string): void {
                selected.value = element;
                emit('update:modelValue', selected.value);
                options.value = [];
                if (props.clearSearchAfterSelection) {
                    clearSelection();
                }
            }

            async function handleUpdatedValue(element: string): Promise<void> {
                if (element) {
                    emit('autocomplete:select', element);
                    if (props.focusAfterSelect) {
                        await nextTick();
                        select.value?.searchEl.focus();
                    }
                } else {
                    emit('autocomplete:clear');
                }
            }

            return {
                select,
                selected,
                query,
                options,
                loadingFunction,
                debouncedQuery,
                noOptions,
                iconName,
                onSearch,
                search,
                onSearchBlur,
                onFocus,
                handleSelectValue,
                handleUpdatedValue,
                clearSelection,
            };
        },
    });
